/**
 * standard-document-columns.tsx — wspólna konfiguracja KOLUMN list dokumentów handlowych,
 * wewnętrznych i specjalnych (klasy STANDARD bez satelity i bez stron: `commercial`, `internal`,
 * `specjalne`).
 *
 * Jedno źródło prawdy dla ekranów, które pokazują te dokumenty jako tabelę z kolumnami „jak na
 * liście": strony `CommercialDocs`, `InternalDocs` i `SpecialDocs` oraz sekcji kokpitu decyzji
 * (`pages/processes/decision-sections`, „Grupuj według: Kartoteka"). Zmiana tutaj jest widoczna na
 * wszystkich — kolumny nie mogą się rozjechać.
 *
 * Jeden plik na trzy klasy, bo konfiguracja kolumn wszystkich list jest IDENTYCZNA (te same
 * wykluczenia, te same nadpisania numeru systemowego, tytułu, statusu i pilności, brak remapu sortu,
 * ten sam typ wiersza — wszystkie trzy klasy czytają ten sam deskryptor listy dokumentów „bare" i
 * ten sam formularz ze statusem/pilnością z kodu) — różni je wyłącznie prefiks kluczy i18n kolumn
 * procesu (`COMMERCIAL_PROCESS_LABEL_PREFIX` / `INTERNAL_PROCESS_LABEL_PREFIX` /
 * `SPECIAL_PROCESS_LABEL_PREFIX`). Umowy i pisma mają własne pliki (satelita / kolumna nadawcy,
 * inna szerokość tytułu). Elementy tylko listy dokumentów specjalnych (kalendarz, panel struktury
 * organizacyjnej, zawężenie do typu dokumentu) zostają na stronie `SpecialDocs`.
 *
 * Zawartość: rejestr nadpisań renderu kolumn rdzennych (`STANDARD_DOC_OVERRIDES`), slugi
 * obsługiwane osobno (`STANDARD_DOC_EXCLUDE_SLUGS`), slugi dołączane do `?columns=`
 * (`STANDARD_DOC_ALWAYS_SLUGS`), mapa id kolumny → slug backendu (`STANDARD_DOC_COLUMN_ID_TO_SLUG`)
 * i typ wiersza listy (`StandardDocRow`, z aliasami `CommercialDocRow`/`InternalDocRow`/
 * `SpecialDocRow` dla stron).
 * Kolumny rdzenne/dodatkowe buduje katalog pól
 * (`buildCoreColumns`/`buildExtColumns` z `ext-list-columns`); kolumny procesu — wspólne
 * `process-columns.tsx`.
 */

import { DOCUMENT_URGENCIES, STANDARD_DOCUMENT_STATUSES } from '@saas/shared';

import {
  documentUrgencyLabel,
  standardDocumentStatusLabel,
} from '@/lib/documents/document-select-labels';
import { PROCESS_COLUMN_ID_TO_SLUG } from '@/lib/documents/process-columns';
import { buildStaticSelectOverride, type CoreColumnOverride } from '@/lib/ext-list-columns';

/** Prefiks kluczy i18n kolumn procesu dokumentów handlowych (patrz `buildProcessColumns`). */
export const COMMERCIAL_PROCESS_LABEL_PREFIX = 'tenant.documents.commercial';

/** Prefiks kluczy i18n kolumn procesu dokumentów wewnętrznych (patrz `buildProcessColumns`). */
export const INTERNAL_PROCESS_LABEL_PREFIX = 'tenant.documents.internal';

/** Prefiks kluczy i18n kolumn procesu dokumentów specjalnych (patrz `buildProcessColumns`). */
export const SPECIAL_PROCESS_LABEL_PREFIX = 'tenant.documents.special';

/**
 * Slugi rdzenne obsługiwane OSOBNO (dedykowane budowniczowie: firma/opiekun z filtrami
 * record-select, źródło z tłumaczoną etykietą `DOC_SOURCE_VALUES`), więc pomijane przez
 * `buildCoreColumns` — inaczej powstałaby druga kolumna na to samo pole. Firma/opiekun →
 * `buildCompanyColumn`/`buildDocumentCoreRefColumns`; źródło →
 * `buildDocumentCoreRefColumns({ withSource: true })` (i18n label z fallbackiem do surowej
 * wartości dla źródeł spoza enumu), NIE rejestr nadpisań (w odróżnieniu od faktur zakupu, gdzie
 * „źródło" ma inny słownik — `SourceBadge` — i idzie przez override).
 */
export const STANDARD_DOC_EXCLUDE_SLUGS: ReadonlySet<string> = new Set([
  'company_id',
  'owner_user_id',
  'source',
]);

/**
 * Slugi dołączane do `?columns=` ZAWSZE, niezależnie od widocznych kolumn: `gcrecord` — czyta go
 * akcja wiersza (restore/delete), nie jest kolumną.
 */
export const STANDARD_DOC_ALWAYS_SLUGS: readonly string[] = ['gcrecord'];

/**
 * Mapa id kolumny → slug backendu dla `?columns=` — część STAŁA: dedykowane buildery spoza
 * katalogu (firma ma id `company`, reszta id = slug 1:1) i kolumny procesu. Kolumny z katalogu
 * (`buildCoreColumns`) dopisuje wołający pętlą `id = slug`.
 */
export const STANDARD_DOC_COLUMN_ID_TO_SLUG: Readonly<Record<string, string>> = {
  company: 'company_id',
  category: 'category',
  owner_user_id: 'owner_user_id',
  source: 'source',
  ...PROCESS_COLUMN_ID_TO_SLUG,
};

/**
 * Rejestr nadpisań kolumn rdzennych dokumentów handlowych, wewnętrznych i specjalnych — render
 * bogaty tam, gdzie generyk (typ→komórka) nie wystarcza: numer systemowy (mono) i tytuł
 * (pogrubiony, nieukrywalny — jedyne pole GWARANTOWANE w każdej odpowiedzi razem z `id`/`gcrecord`)
 * oraz status i pilność — zamknięte enumy z kodu (nie słownik tenanta), więc `buildStaticSelectOverride`:
 * lista wyboru w wierszu filtrów i etykieta w komórce z jednej funkcji (`document-select-labels`).
 * Data dokumentu renderuje się generykiem (`buildCoreColumns`). Sortowalność bierze z
 * `ctx.resolveSortable(ctx.field)` (ta sama serwerowa reguła co generyk — id kolumny = slug, brak
 * potrzeby remapu sortu). Klucze = slugi pól z katalogu (`system_module_fields.slug`).
 */
export const STANDARD_DOC_OVERRIDES: Readonly<Record<string, CoreColumnOverride<StandardDocRow>>> =
  {
    system_number: ({ field, accessor, resolveSortable }) => ({
      id: field.slug,
      accessor,
      header: field.name,
      defaultVisible: true,
      defaultWidth: 160,
      sortable: resolveSortable?.(field) ?? false,
      headerFilter: { category: 'text', fieldSlug: 'system_number' },
      renderCell: (value) => (
        <span className="font-mono text-xs text-text-secondary">{value ? String(value) : '—'}</span>
      ),
    }),
    title: ({ field, accessor, resolveSortable }) => ({
      id: field.slug,
      accessor,
      header: field.name,
      defaultVisible: true,
      defaultWidth: 320,
      hideable: false,
      sortable: resolveSortable?.(field) ?? false,
      headerFilter: { category: 'text', fieldSlug: 'title' },
      renderCell: (value) => (
        <span className="font-medium text-text-primary">{value ? String(value) : '—'}</span>
      ),
    }),
    status: buildStaticSelectOverride<StandardDocRow>({
      codes: STANDARD_DOCUMENT_STATUSES,
      labelOf: standardDocumentStatusLabel,
    }),
    urgency: buildStaticSelectOverride<StandardDocRow>({
      codes: DOCUMENT_URGENCIES,
      labelOf: documentUrgencyLabel,
    }),
  };

/**
 * `id`, `gcrecord` (zawsze w `alwaysSlugs`) i `title` (kolumna `hideable: false`) są jedynymi polami
 * GWARANTOWANYMI w każdej odpowiedzi — reszta zależy od tego, co user aktualnie widzi (Kreator
 * Listy może schować każdą inną kolumnę, a backend w trybie projekcji `?columns=` zwraca WYŁĄCZNIE
 * zażądane pola). Typ wiersza list dokumentów handlowych, wewnętrznych i specjalnych; wiersz sekcji
 * kokpitu decyzji = ten wiersz + kroki decyzji.
 */
export interface StandardDocRow {
  id: string;
  companyId?: string;
  systemNumber?: string | null;
  title: string | null;
  status?: string;
  urgency?: string;
  description?: string | null;
  notes?: string | null;
  rejectionReason?: string | null;
  ownerUserId?: string | null;
  source?: string;
  externalId?: string | null;
  externalSource?: string | null;
  documentDate?: string | null;
  counterpartyNameCached?: string | null;
  counterpartyTaxIdCached?: string | null;
  contractorId?: string | null;
  /** Nazwa kategorii z `record_categories` (LEFT JOIN w deskryptorze) — kolumna „Kategoria". */
  categoryName?: string | null;
  gcrecord: string | null;
  createdAt?: string;
  updatedAt?: string;
  createdByUserId?: string;
  updatedByUserId?: string;
  /**
   * Kolumny przekrojowe silnika list (`apps/api/lib/list-engine/providers/process-columns.ts`)
   * — DOKŁADANE na każdym wierszu gdy tenant ma aktywny moduł `processes`, POMIJANE
   * całkowicie (klucz nieobecny) gdy moduł nieaktywny. `currentStage` to bieżące etapy sprawy
   * w toku (0..N, gałęzie równoległe) albo nazwa etapu końcowego sprawy zakończonej (jeden element).
   */
  activeProcessName?: string | null;
  currentStage?: string[];
  /**
   * Wartości pól dodatkowych (ext) — obecne TYLKO gdy backend odpowiada w trybie
   * projekcji `?columns=` i zażądano ≥1 aktywnego pola ext (kontrakt jak
   * `GET /api/products?columns=` / `GET /api/contractors?columns=`).
   */
  ext?: Record<string, unknown>;
}

/** Wiersz listy dokumentów handlowych — kształt wspólny z wewnętrznymi (`StandardDocRow`). */
export type CommercialDocRow = StandardDocRow;

/** Wiersz listy dokumentów wewnętrznych — kształt wspólny z handlowymi (`StandardDocRow`). */
export type InternalDocRow = StandardDocRow;

/** Wiersz listy dokumentów specjalnych — kształt wspólny z handlowymi (`StandardDocRow`). */
export type SpecialDocRow = StandardDocRow;
